import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { FinancePeriod } from '@garage/shared'
import { useFormatters } from '@/lib/hooks.js'

interface CashFlowChartProps {
  series: FinancePeriod[]
}

// Padding, radii and gaps below are real CSS pixels: the SVG's viewBox is set
// to the wrapper's own measured pixel width (see the ResizeObserver below),
// so one viewBox unit is always exactly one on-screen pixel. That is what the
// previous version got wrong — it laid marks out in a 0-100 box and then
// rescaled x and y by *different* factors (10 and 7, from a hardcoded
// 1000x700 viewBox) to reach pixels, so a "padding" meant as a small gutter
// ended up as roughly half the canvas.
const PADDING = { top: 16, right: 16, bottom: 40, left: 56 }
const CORNER_RADIUS = 4
const SEGMENT_GAP = 2
const MAX_BAR_WIDTH = 48
const DEFAULT_WIDTH = 600

// Width:height ratio of the plot, expressed both here (for the viewBox math)
// and as the identical Tailwind `aspect-[…]` class below (for the box's
// actual on-screen size). Height is therefore ALWAYS a pure function of
// width, which is itself set from outside (the grid column) — never the
// other way round. The previous version measured a `h-full` element whose
// height came from its own content, which depended on that same measurement:
// an unbounded feedback loop that inflated the panel to thousands of pixels
// tall with only the single largest bar visible.
const ASPECT_RATIO = 12 / 5

/**
 * Rounds a raw step DOWN to the nearest 1/2/5 x a power of ten (never up), so
 * that `divisions` many steps always land inside [0, maxValue] — rounding up
 * here would push the top tick just past the plot's top edge, where the SVG
 * clips it silently and one of the three gridlines simply goes missing.
 */
function niceStep(maxValue: number, divisions: number): number {
  if (maxValue <= 0) return 1
  const rawStep = maxValue / divisions
  const magnitude = 10 ** Math.floor(Math.log10(rawStep))
  const residual = rawStep / magnitude
  const niceResidual = residual >= 5 ? 5 : residual >= 2 ? 2 : 1
  return niceResidual * magnitude
}

function getMonthLabel(period: string, language: string): string {
  const parts = period.split('-')
  if (parts.length < 2 || !parts[0] || !parts[1]) return period
  const date = new Date(parseInt(parts[0]!), parseInt(parts[1]!) - 1, 1)
  return date.toLocaleDateString(language === 'ar' ? 'ar-u-nu-latn' : 'en-US', { month: 'short' })
}

/** Path for a rectangle rounded on its top two corners only, clamped so the radius never exceeds the shape. */
function roundedTopRect(x: number, top: number, width: number, height: number, radius: number): string {
  const r = Math.max(0, Math.min(radius, height / 2, width / 2))
  return `
    M ${x} ${top + height}
    L ${x} ${top + r}
    Q ${x} ${top} ${x + r} ${top}
    L ${x + width - r} ${top}
    Q ${x + width} ${top} ${x + width} ${top + r}
    L ${x + width} ${top + height}
    Z
  `
}

interface HoverState {
  index: number | null
}

/**
 * Stacked column chart of cash received (cost below, profit above) over time.
 * Renders as hand-written inline SVG; no external charting library.
 */
export function CashFlowChart({ series }: CashFlowChartProps) {
  const formatters = useFormatters()
  const { t, i18n } = useTranslation()
  const [hover, setHover] = useState<HoverState>({ index: null })
  const wrapperRef = useRef<HTMLDivElement>(null)
  const [measuredWidth, setMeasuredWidth] = useState(DEFAULT_WIDTH)

  // Observe WIDTH only. Height is derived from it (below) and never fed back
  // into anything this observer watches, so there is no cycle: the wrapper's
  // box size comes from the `aspect-[12/5]` class purely as a function of its
  // (externally determined) width, and we merely read that width back to
  // drive the SVG's internal coordinate math at full pixel precision.
  useEffect(() => {
    const el = wrapperRef.current
    if (!el) return
    const observer = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width
      if (w && w > 0) setMeasuredWidth(w)
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  const width = measuredWidth
  const height = width / ASPECT_RATIO
  const chartWidth = Math.max(width - PADDING.left - PADDING.right, 1)
  const chartHeight = Math.max(height - PADDING.top - PADDING.bottom, 1)
  const baselineY = height - PADDING.bottom

  const colWidth = chartWidth / (series.length || 1)
  const barWidth = Math.min(colWidth * 0.6, MAX_BAR_WIDTH)

  // Stack height per month is cost + profit (== net revenue); scale off the
  // tallest stack so it fills ~90% of the plot, never dividing by zero.
  const maxStack = Math.max(...series.map((s) => s.costMinor + s.profitMinor), 0)
  const scaleMax = maxStack > 0 ? maxStack / 0.9 : 1
  const toHeight = (value: number) => (value / scaleMax) * chartHeight

  const rawTicks = maxStack > 0 ? [0, niceStep(scaleMax, 2), niceStep(scaleMax, 2) * 2] : [0]
  const seenLabels = new Set<string>()
  const ticks = rawTicks
    .map((value) => ({ value, label: formatters.compact(value) }))
    .filter((tick) => {
      if (seenLabels.has(tick.label)) return false
      seenLabels.add(tick.label)
      return true
    })

  // Every bar is always drawn, at every window length (3/6/12/24 months); only
  // the LABEL underneath it may be thinned, since that's what actually
  // collides at narrow widths. One tier is rarely enough on its own — a
  // 24-month window on a phone still overlaps at "every other" — so the
  // stride escalates with how little room each column actually has.
  const labelStride = colWidth < 22 ? 3 : colWidth < 40 ? 2 : 1

  const hoveredData = hover.index !== null ? series[hover.index] : null

  return (
    <div className="w-full flex flex-col gap-3">
      <div ref={wrapperRef} className="w-full aspect-[12/5] min-h-[200px]">
        <svg
          viewBox={`0 0 ${width} ${height}`}
          preserveAspectRatio="xMidYMid meet"
          className="w-full h-full"
          role="img"
          aria-label={t('financeChart.cashFlowChartLabel', { from: series[0]?.period, to: series[series.length - 1]?.period })}
          onMouseLeave={() => setHover({ index: null })}
        >
          {/* Background */}
          <rect width={width} height={height} fill="var(--c-surface-lowest)" />

          {/* Gridlines and y-axis labels, evenly spaced up from the baseline */}
          {ticks.map((tick) => {
            const y = baselineY - toHeight(tick.value)
            return (
              <g key={tick.value}>
                {tick.value > 0 && (
                  <line
                    x1={PADDING.left}
                    y1={y}
                    x2={width - PADDING.right}
                    y2={y}
                    stroke="var(--c-outline-variant)"
                    strokeWidth={1}
                    strokeDasharray="4,4"
                  />
                )}
                <text
                  x={PADDING.left - 8}
                  y={y}
                  dominantBaseline="middle"
                  textAnchor="end"
                  fill="currentColor"
                  className="text-label-sm text-on-surface-variant"
                >
                  {tick.label}
                </text>
              </g>
            )
          })}

          {/* X-axis / baseline */}
          <line
            x1={PADDING.left}
            y1={baselineY}
            x2={width - PADDING.right}
            y2={baselineY}
            stroke="var(--c-outline-variant)"
            strokeWidth={1}
          />

          {/* Bars, one stacked column per month, growing up from the baseline. Every month in the window gets a bar, regardless of label thinning. */}
          {series.map((period, idx) => {
            const xCenter = PADDING.left + (idx + 0.5) * colWidth
            const x = xCenter - barWidth / 2
            const costHeight = toHeight(period.costMinor)
            const profitHeight = toHeight(period.profitMinor)
            const hasGap = period.costMinor > 0 && period.profitMinor > 0
            const gap = hasGap ? SEGMENT_GAP : 0
            const costTop = baselineY - costHeight
            const profitTop = costTop - gap - profitHeight

            return (
              <g key={period.period}>
                {period.costMinor > 0 && (
                  <rect x={x} y={costTop} width={barWidth} height={costHeight} fill="var(--c-surface-highest)" />
                )}
                {period.profitMinor > 0 && (
                  <path
                    d={roundedTopRect(x, profitTop, barWidth, profitHeight, CORNER_RADIUS)}
                    fill="var(--c-on-surface)"
                  />
                )}
                {/* Full-height hit target so hover works anywhere in the column, not just on the bar itself */}
                <rect
                  x={PADDING.left + idx * colWidth}
                  y={PADDING.top}
                  width={colWidth}
                  height={chartHeight}
                  fill="transparent"
                  onMouseEnter={() => setHover({ index: idx })}
                />
              </g>
            )
          })}

          {/* Month labels, centred under their bars; thinned (not dropped) when narrow */}
          {series.map((period, idx) => {
            if (idx % labelStride !== 0) return null
            const xCenter = PADDING.left + (idx + 0.5) * colWidth
            return (
              <text
                key={period.period}
                x={xCenter}
                y={baselineY + 20}
                textAnchor="middle"
                fill="currentColor"
                className="text-label-sm text-on-surface-variant"
              >
                {getMonthLabel(period.period, i18n.language)}
              </text>
            )
          })}

          {/* Tooltip, anchored to the hovered column and clamped inside the plot */}
          {hoveredData &&
            hover.index !== null &&
            (() => {
              const xCenter = PADDING.left + (hover.index! + 0.5) * colWidth
              const stackHeight = toHeight(hoveredData.costMinor + hoveredData.profitMinor)
              const stackTop =
                baselineY - stackHeight - (hoveredData.costMinor > 0 && hoveredData.profitMinor > 0 ? SEGMENT_GAP : 0)
              const tooltipWidth = 152
              const tooltipHeight = 100
              const tooltipX = Math.min(
                Math.max(xCenter - tooltipWidth / 2, PADDING.left),
                width - PADDING.right - tooltipWidth,
              )
              const tooltipY = Math.max(stackTop - tooltipHeight - 8, PADDING.top)

              return (
                <g pointerEvents="none">
                  <rect
                    x={tooltipX}
                    y={tooltipY}
                    width={tooltipWidth}
                    height={tooltipHeight}
                    rx={6}
                    fill="var(--c-surface-highest)"
                    stroke="var(--c-outline-variant)"
                    strokeWidth={1}
                  />
                  <text x={tooltipX + 12} y={tooltipY + 20} fill="currentColor" className="text-label-md text-on-surface font-semibold">
                    {getMonthLabel(hoveredData.period, i18n.language)}
                  </text>
                  <text x={tooltipX + 12} y={tooltipY + 39} fill="currentColor" className="text-label-sm text-on-surface-variant">
                    {t('financeChart.cash')}: {formatters.amount(hoveredData.cashReceivedMinor)}
                  </text>
                  <text x={tooltipX + 12} y={tooltipY + 56} fill="currentColor" className="text-label-sm text-on-surface-variant">
                    {t('financeChart.net')}: {formatters.amount(hoveredData.revenueMinor)}
                  </text>
                  <text x={tooltipX + 12} y={tooltipY + 73} fill="currentColor" className="text-label-sm text-on-surface-variant">
                    {t('financeChart.cost')}: {formatters.amount(hoveredData.costMinor)}
                  </text>
                  <text x={tooltipX + 12} y={tooltipY + 90} fill="currentColor" className="text-label-sm text-on-surface-variant">
                    {t('financeChart.profit')}: {formatters.amount(hoveredData.profitMinor)}
                  </text>
                </g>
              )
            })()}
        </svg>
      </div>

      {/* Legend */}
      <div className="flex gap-6 text-label-md text-on-surface-variant">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: 'var(--c-surface-highest)' }} />
          <span>{t('financeChart.cost')}</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: 'var(--c-on-surface)' }} />
          <span>{t('financeChart.profit')}</span>
        </div>
      </div>
    </div>
  )
}
